import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, TrendingUp, Users, Globe, FileText, BookOpen,
  UserSearch, Mail, Settings, HelpCircle, Bell, ChevronDown,
  ChevronRight, Star, Zap, ArrowUp, ArrowDown, Menu, X,
  CheckCircle, Clock, AlertCircle, ExternalLink, Bot,
  BarChart2, Target, Newspaper, Send, LogOut, User, CreditCard,
  Info, Award, Lock, MapPin, Link2, RefreshCw, Filter, Search,
  Plus, MoreHorizontal, Eye, Download, Calendar
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, BarChart, Bar, Cell
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Platform definitions — 11 platforms, weights sum to 100% ─────────
const PLATFORM_META = [
  {
    key: "chatgpt",
    name: "ChatGPT",
    weight: 20,
    color: "#10a37f",
    logo: "https://cdn.worldvectorlogo.com/logos/chatgpt-6.svg",
  },
  {
    key: "gemini",
    name: "Google Gemini",
    weight: 12,
    color: "#4285F4",
    logo: "https://www.gstatic.com/lamda/images/gemini_sparkle_v002_d4735304ff6292a690345.svg",
  },
  {
    key: "google_ai",
    name: "Google AI",
    weight: 10,
    color: "#EA4335",
    logo: "https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_272x92dp.png",
  },
  {
    key: "perplexity",
    name: "Perplexity",
    weight: 8,
    color: "#20B8CD",
    logo: "https://cdn.brandfetch.io/idNdawysz-/w/400/h/400/theme/dark/icon.png?c=1dxbfHSJFAPEwTen7NJl5g",
  },
  {
    key: "copilot",
    name: "MS Copilot",
    weight: 8,
    color: "#0078D4",
    logo: "https://cdn.worldvectorlogo.com/logos/microsoft-copilot.svg",
  },
  {
    key: "claude_ai",
    name: "Claude",
    weight: 8,
    color: "#D4764C",
    logo: null,
  },
  {
    key: "grok",
    name: "Grok",
    weight: 6,
    color: "#0F172A",
    logo: "https://cdn.worldvectorlogo.com/logos/x-2.svg",
  },
  {
    key: "meta_ai",
    name: "Meta AI",
    weight: 6,
    color: "#0064E0",
    logo: "https://cdn.worldvectorlogo.com/logos/meta-1.svg",
  },
  {
    key: "online_presence",
    name: "Online Presence",
    weight: 10,
    color: "#7C3AED",
    logo: null,
  },
  {
    key: "bing",
    name: "Bing",
    weight: 5,
    color: "#008373",
    logo: "https://cdn.worldvectorlogo.com/logos/bing-1.svg",
  },
  {
    key: "google_maps",
    name: "Google Maps",
    weight: 7,
    color: "#34A853",
    logo: "https://cdn.worldvectorlogo.com/logos/google-maps-2020-icon.svg",
  },
];

// ── Time formatter ─────────────────────────────────────────────────────
const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 2)   return "Just now";
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
};

// ── Platform Logo component ────────────────────────────────────────────
const PlatformLogo = ({ platform, size = 20 }) => {
  const [imgError, setImgError] = useState(false);
  if (!platform.logo || imgError) {
    return (
      <div
        className="rounded-md flex items-center justify-center text-white font-bold flex-shrink-0"
        style={{ width: size, height: size, background: platform.color, fontSize: size * 0.45 }}
      >
        {platform.name[0]}
      </div>
    );
  }
  return (
    <img
      src={platform.logo}
      alt={platform.name}
      width={size}
      height={size}
      className="rounded-md object-contain flex-shrink-0"
      onError={() => setImgError(true)}
      style={{ width: size, height: size }}
    />
  );
};

// ── Central API hook ────────────────────────────────────────────────────
const useDashboardData = (user) => {
  const [data, setData]       = useState({});
  const [loading, setLoading] = useState({});
  const sid  = localStorage.getItem("session_id");
  const hdrs = { ...(sid ? { "X-Session-ID": sid } : {}) };

  const apiFetch = async (key, path) => {
    setLoading(l => ({ ...l, [key]: true }));
    try {
      const res = await fetch(`${API}${path}`, { credentials: "include", headers: hdrs });
      if (res.ok) {
        const json = await res.json();
        setData(d => ({ ...d, [key]: json }));
      }
    } catch {}
    finally { setLoading(l => ({ ...l, [key]: false })); }
  };

  useEffect(() => {
    if (!user?.user_id) return;
    const isPro = ["growth","dfy","dfy_monthly","dfy_yearly"].includes(user.plan_intent || user.subscription_tier);
    apiFetch("overview",      "/dashboard");
    apiFetch("score",         "/score/details");
    apiFetch("competitors",   "/competitors");
    apiFetch("submissions",   "/submissions");
    apiFetch("pressReleases", "/press-releases");
    apiFetch("blogPosts",     "/blog-posts");
    apiFetch("notifications", "/notifications");
    if (isPro) {
      apiFetch("leads",  "/leads");
      apiFetch("emails", "/outreach-emails");
    }
  }, [user?.user_id]);

  const refresh = (key, path) => apiFetch(key, path);
  return { data, loading, refresh };
};

// ── StatusBadge ────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    live:        { label: "Live",        cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    published:   { label: "Published",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    pending:     { label: "Pending",     cls: "bg-amber-50 text-amber-700 border-amber-200" },
    submitted:   { label: "Submitted",   cls: "bg-blue-50 text-blue-700 border-blue-200" },
    not_started: { label: "Not Started", cls: "bg-slate-50 text-slate-500 border-slate-200" },
    reviewing:   { label: "In Review",   cls: "bg-violet-50 text-violet-700 border-violet-200" },
    ready:       { label: "Ready",       cls: "bg-blue-50 text-blue-700 border-blue-200" },
    generating:  { label: "Generating",  cls: "bg-slate-50 text-slate-500 border-slate-200" },
    rejected:    { label: "Rejected",    cls: "bg-red-50 text-red-600 border-red-200" },
    done:        { label: "Done",        cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    in_progress: { label: "In Progress", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  };
  const s = map[status] || map.not_started;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${s.cls}`}>
      {s.label}
    </span>
  );
};

// ── Score ring ─────────────────────────────────────────────────────────
const ScoreRing = ({ score = 0, max = 1000, size = 120 }) => {
  const pct   = Math.min(score / max, 1);
  const r     = (size / 2) - 10;
  const circ  = 2 * Math.PI * r;
  const color = score >= 700 ? "#10B981" : score >= 400 ? "#F59E0B" : "#EF4444";
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#F1F5F9" strokeWidth="8"/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circ} strokeDashoffset={circ - pct * circ}
          strokeLinecap="round" style={{ transition: "stroke-dashoffset 1.2s ease" }}/>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-slate-900" style={{ color }}>{score}</span>
        <span className="text-xs text-slate-400">/ {max}</span>
      </div>
    </div>
  );
};

// ── Stat card (like Shopeers top row) ─────────────────────────────────
const StatCard = ({ label, value, change, suffix = "", icon: Icon, iconColor }) => {
  const isUp = change >= 0;
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-slate-500">{label}</span>
        {Icon && (
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: iconColor + "18" }}>
            <Icon className="w-4 h-4" style={{ color: iconColor }}/>
          </div>
        )}
      </div>
      <div className="flex items-end gap-2">
        <span className="text-2xl font-bold text-slate-900">{value}{suffix}</span>
        {change !== undefined && (
          <span className={`text-xs font-medium mb-0.5 flex items-center gap-0.5 ${isUp ? "text-emerald-600" : "text-red-500"}`}>
            {isUp ? <ArrowUp className="w-3 h-3"/> : <ArrowDown className="w-3 h-3"/>}
            {Math.abs(change)}
          </span>
        )}
      </div>
      {change !== undefined && (
        <p className="text-xs text-slate-400 mt-1">vs last scan</p>
      )}
    </div>
  );
};

// ── Nav item ───────────────────────────────────────────────────────────
const NavItem = ({ icon: Icon, label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all mb-0.5 ${
      active
        ? "bg-blue-600 text-white font-medium"
        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
    }`}
  >
    <Icon className="w-4 h-4 flex-shrink-0"/>
    <span className="flex-1 text-left">{label}</span>
    {badge && (
      <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-white/20 text-white" : "bg-blue-100 text-blue-700"}`}>
        {badge}
      </span>
    )}
  </button>
);

// ── Section label ──────────────────────────────────────────────────────
const NavSection = ({ label }) => (
  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mb-1 mt-5">{label}</p>
);

// ── Loading spinner ────────────────────────────────────────────────────
const Spinner = () => (
  <div className="flex items-center justify-center min-h-48">
    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"/>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════
// AI SCORE SECTION
// ═══════════════════════════════════════════════════════════════════════
const AIScoreSection = ({ score, isPro, scoreData = {}, loading }) => {
  const platformScores = scoreData.platform_scores || {};
  const actionPlan     = scoreData.action_plan || [];
  const history        = scoreData.score_history || [];

  if (loading) return <Spinner/>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">AI Score</h2>
          <p className="text-sm text-slate-500 mt-0.5">Your visibility across AI search platforms</p>
        </div>
      </div>

      {/* Score overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Big score card */}
        <div className="bg-white rounded-lg border border-slate-200 p-6 flex flex-col items-center justify-center gap-4">
          <ScoreRing score={score} size={140}/>
          <div className="text-center">
            <p className="text-sm font-semibold text-slate-900">
              {score >= 700 ? "Strong Visibility" : score >= 400 ? "Moderate Visibility" : "Low Visibility"}
            </p>
            <p className="text-xs text-slate-400 mt-1">Updated weekly every Monday</p>
          </div>
        </div>

        {/* Score history chart */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Score History</h3>
            <span className="text-xs text-slate-400">Last 8 scans</span>
          </div>
          {history.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={history}>
                <defs>
                  <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#2563EB" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#2563EB" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F8FAFC" vertical={false}/>
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} domain={["auto","auto"]}/>
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "6px", fontSize: "12px" }} formatter={(v) => [`${v} pts`, "Score"]}/>
                <Area type="monotone" dataKey="score" stroke="#2563EB" strokeWidth={2} fill="url(#sg)" dot={false} activeDot={{ r: 4, fill: "#2563EB" }}/>
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-44 text-sm text-slate-400">Scan data will appear after your first weekly scan</div>
          )}
        </div>
      </div>

      {/* Platform breakdown — full grid with real logos */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-semibold text-slate-900">Platform Breakdown</h3>
          <div className="text-xs text-slate-400">Weighted score contribution</div>
        </div>
        <div className="space-y-4">
          {PLATFORM_META.map((p) => {
            const raw   = platformScores[p.key] ?? 0;
            const pct   = Math.min(raw, 100);
            const pts   = Math.round((pct / 100) * (p.weight * 10));
            const maxPts = p.weight * 10;
            return (
              <div key={p.key} className="flex items-center gap-4">
                <div className="flex items-center gap-2.5 w-36 flex-shrink-0">
                  <PlatformLogo platform={p} size={22}/>
                  <span className="text-sm text-slate-700 truncate">{p.name}</span>
                </div>
                <div className="flex-1 bg-slate-100 rounded-full h-1.5">
                  <div
                    className="h-1.5 rounded-full transition-all duration-700"
                    style={{ width: `${pct}%`, background: p.color }}
                  />
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs text-slate-400 w-10 text-right">{pct}%</span>
                  <span className="text-xs font-semibold text-slate-700 w-16 text-right">{pts}/{maxPts} pts</span>
                  <span className="text-xs text-slate-400 w-12 text-right">{p.weight}% weight</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Weight explanation */}
        <div className="mt-5 pt-5 border-t border-slate-100">
          <p className="text-xs text-slate-400 leading-relaxed">
            Score is calculated by weighting each platform's visibility score by its share of total AI search usage. ChatGPT powers ~20% of AI queries, so it carries the most weight. Online Presence accounts for directory listings, reviews, and citations that AI systems use to verify your business.
          </p>
        </div>
      </div>

      {/* Action Plan */}
      {actionPlan.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Action Plan</h3>
          <div className="space-y-3">
            {actionPlan.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-md bg-slate-50 border border-slate-100">
                <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${item.impact === "high" ? "bg-red-400" : item.impact === "medium" ? "bg-amber-400" : "bg-slate-300"}`}/>
                <p className="text-sm text-slate-700 flex-1">{item.action}</p>
                <span className={`text-xs font-medium px-2 py-0.5 rounded flex-shrink-0 ${item.impact === "high" ? "bg-red-50 text-red-600" : item.impact === "medium" ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-500"}`}>
                  {item.impact}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// COMPETITORS SECTION
// ═══════════════════════════════════════════════════════════════════════
const CompetitorsSection = ({ isPro, data = {}, loading }) => {
  const competitors  = data.competitors || [];
  const userScore    = data.user_score || 0;
  const userPlatform = data.user_platforms || {};

  if (loading) return <Spinner/>;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-slate-900">Competitors</h2>
        <p className="text-sm text-slate-500 mt-0.5">See how you rank against similar businesses</p>
      </div>

      {competitors.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
          <Users className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
          <p className="text-sm font-medium text-slate-600">No competitors tracked yet</p>
          <p className="text-xs text-slate-400 mt-1">Add competitors during onboarding or contact support</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left text-xs font-semibold text-slate-500 px-5 py-3">Business</th>
                <th className="text-left text-xs font-semibold text-slate-500 px-5 py-3">Score</th>
                {PLATFORM_META.slice(0, 4).map(p => (
                  <th key={p.key} className="text-left text-xs font-semibold text-slate-500 px-3 py-3 hidden lg:table-cell">
                    <div className="flex items-center gap-1.5">
                      <PlatformLogo platform={p} size={14}/>
                      <span>{p.name.split(" ")[0]}</span>
                    </div>
                  </th>
                ))}
                <th className="text-left text-xs font-semibold text-slate-500 px-5 py-3">vs You</th>
              </tr>
            </thead>
            <tbody>
              {/* Your row */}
              <tr className="border-b border-slate-100 bg-blue-50/40">
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded bg-blue-600 flex items-center justify-center text-white text-xs font-bold">Y</div>
                    <span className="text-sm font-semibold text-slate-900">Your Business</span>
                  </div>
                </td>
                <td className="px-5 py-3.5">
                  <span className="text-sm font-bold text-blue-600">{userScore}</span>
                </td>
                {PLATFORM_META.slice(0, 4).map(p => (
                  <td key={p.key} className="px-3 py-3.5 hidden lg:table-cell">
                    <span className="text-xs text-slate-600">{userPlatform[p.key] ?? "—"}</span>
                  </td>
                ))}
                <td className="px-5 py-3.5">
                  <span className="text-xs text-blue-600 font-medium">—</span>
                </td>
              </tr>
              {competitors.map((c, i) => {
                const diff = (c.master_score || 0) - userScore;
                return (
                  <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded bg-slate-200 flex items-center justify-center text-slate-600 text-xs font-bold">
                          {(c.name || c.url || "?")[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{c.name || c.url}</p>
                          {c.city && <p className="text-xs text-slate-400">{c.city}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm font-semibold text-slate-900">{c.master_score || 0}</span>
                    </td>
                    {PLATFORM_META.slice(0, 4).map(p => (
                      <td key={p.key} className="px-3 py-3.5 hidden lg:table-cell">
                        <span className="text-xs text-slate-600">{c.platform_scores?.[p.key] ?? "—"}</span>
                      </td>
                    ))}
                    <td className="px-5 py-3.5">
                      <span className={`text-xs font-semibold ${diff > 0 ? "text-red-500" : "text-emerald-600"}`}>
                        {diff > 0 ? `+${diff}` : diff} pts
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// SUBMISSIONS SECTION
// ═══════════════════════════════════════════════════════════════════════
const SubmissionsSection = ({ data = {}, loading, refresh }) => {
  const submissions = data.submissions || [];
  const counts      = data.counts || {};

  if (loading) return <Spinner/>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Directory Submissions</h2>
          <p className="text-sm text-slate-500 mt-0.5">Your business listings across the web</p>
        </div>
        <button onClick={refresh} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-md px-3 py-1.5 bg-white">
          <RefreshCw className="w-3 h-3"/> Refresh
        </button>
      </div>

      {/* Count row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Total",     value: submissions.length, color: "#2563EB" },
          { label: "Live",      value: counts.live || 0,      color: "#10B981" },
          { label: "Submitted", value: counts.submitted || 0, color: "#F59E0B" },
          { label: "Pending",   value: counts.pending || 0,   color: "#94A3B8" },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-lg border border-slate-200 p-4">
            <p className="text-xs text-slate-500 mb-1">{s.label}</p>
            <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {submissions.length === 0 ? (
          <div className="p-12 text-center">
            <Globe className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
            <p className="text-sm text-slate-500">Submissions will appear here once your first scan runs</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left text-xs font-semibold text-slate-500 px-5 py-3">Platform</th>
                <th className="text-left text-xs font-semibold text-slate-500 px-5 py-3">Status</th>
                <th className="text-left text-xs font-semibold text-slate-500 px-5 py-3 hidden sm:table-cell">Submitted</th>
                <th className="text-left text-xs font-semibold text-slate-500 px-5 py-3">Link</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((s, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3.5">
                    <span className="text-sm font-medium text-slate-900">{s.platform || s.directory}</span>
                  </td>
                  <td className="px-5 py-3.5"><StatusBadge status={s.status}/></td>
                  <td className="px-5 py-3.5 hidden sm:table-cell">
                    <span className="text-xs text-slate-400">{s.submitted_at ? new Date(s.submitted_at).toLocaleDateString() : "—"}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    {s.live_url ? (
                      <a href={s.live_url} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                        View <ExternalLink className="w-3 h-3"/>
                      </a>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// PRESS RELEASES SECTION
// ═══════════════════════════════════════════════════════════════════════
const PressReleasesSection = ({ isPro, data = {}, loading, refresh }) => {
  const [expanded, setExpanded] = useState(null);
  const prs = data.press_releases || [];

  if (loading) return <Spinner/>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Press Releases</h2>
          <p className="text-sm text-slate-500 mt-0.5">AI-generated press releases submitted on your behalf</p>
        </div>
        <button onClick={refresh} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-md px-3 py-1.5 bg-white">
          <RefreshCw className="w-3 h-3"/> Refresh
        </button>
      </div>

      {prs.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
          <Newspaper className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
          <p className="text-sm font-medium text-slate-600">Your first press release is being generated</p>
          <p className="text-xs text-slate-400 mt-1">It will appear here within 24 hours for review</p>
        </div>
      ) : (
        <div className="space-y-3">
          {prs.map((pr, i) => (
            <div key={i} className="bg-white rounded-lg border border-slate-200">
              <div className="flex items-center gap-4 px-5 py-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <StatusBadge status={pr.status}/>
                    {pr.outlet && <span className="text-xs text-slate-400">{pr.outlet}</span>}
                  </div>
                  <p className="text-sm font-medium text-slate-900 truncate">{pr.title || "Press Release"}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{pr.created_at ? new Date(pr.created_at).toLocaleDateString() : ""}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {pr.live_url && (
                    <a href={pr.live_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                      View <ExternalLink className="w-3 h-3"/>
                    </a>
                  )}
                  <button onClick={() => setExpanded(expanded === i ? null : i)}
                    className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">
                    {expanded === i ? "Hide" : "Preview"} <ChevronRight className={`w-3 h-3 transition-transform ${expanded === i ? "rotate-90" : ""}`}/>
                  </button>
                </div>
              </div>
              {expanded === i && pr.content && (
                <div className="px-5 pb-4 border-t border-slate-100">
                  <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap mt-3">{pr.content}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// BLOG POSTS SECTION
// ═══════════════════════════════════════════════════════════════════════
const BlogPostsSection = ({ isPro, data = {}, loading, refresh }) => {
  const [expanded, setExpanded] = useState(null);
  const posts = data.posts || [];

  if (loading) return <Spinner/>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Blog Posts</h2>
          <p className="text-sm text-slate-500 mt-0.5">GEO + SEO optimized content written for your business</p>
        </div>
        <button onClick={refresh} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-md px-3 py-1.5 bg-white">
          <RefreshCw className="w-3 h-3"/> Refresh
        </button>
      </div>

      {posts.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
          <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
          <p className="text-sm font-medium text-slate-600">Your first blog post is being written</p>
          <p className="text-xs text-slate-400 mt-1">AI is tailoring content to your business and location</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post, i) => {
            const statusMap = {
              published: "bg-emerald-50 text-emerald-700 border-emerald-200",
              ready:     "bg-blue-50 text-blue-700 border-blue-200",
              reviewing: "bg-violet-50 text-violet-700 border-violet-200",
              generating:"bg-slate-50 text-slate-500 border-slate-200",
              rejected:  "bg-red-50 text-red-600 border-red-200",
            };
            return (
              <div key={i} className="bg-white rounded-lg border border-slate-200">
                <div className="flex items-center gap-4 px-5 py-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <StatusBadge status={post.status}/>
                      {isPro && <span className="text-xs text-slate-400">Growth — auto-published</span>}
                    </div>
                    <p className="text-sm font-medium text-slate-900 truncate">{post.title || "Blog Post"}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{post.created_at ? new Date(post.created_at).toLocaleDateString() : ""}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {post.live_url && (
                      <a href={post.live_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                        View live <ExternalLink className="w-3 h-3"/>
                      </a>
                    )}
                    <button onClick={() => setExpanded(expanded === i ? null : i)}
                      className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">
                      {expanded === i ? "Hide" : "Preview"} <ChevronRight className={`w-3 h-3 transition-transform ${expanded === i ? "rotate-90" : ""}`}/>
                    </button>
                  </div>
                </div>
                {expanded === i && post.content && (
                  <div className="px-5 pb-4 border-t border-slate-100">
                    <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap mt-3">{post.content}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* How it works */}
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">How blog posts work</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { step: "01", title: "AI Writes", desc: "Claude generates a GEO and SEO optimized post tailored to your niche, location, and target audience", color: "#7C3AED" },
            { step: "02", title: "We Review", desc: "Our team checks quality before it reaches you", color: "#F59E0B" },
            { step: "03", title: isPro ? "Auto Published" : "You Publish", desc: isPro ? "We publish it to news.theordo.io — live, indexed, linking back to your site" : "You receive the draft to publish on your website", color: "#10B981" },
          ].map((s, i) => (
            <div key={i} className="flex gap-3">
              <div className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0 text-white text-xs font-bold" style={{ background: s.color }}>{s.step}</div>
              <div>
                <p className="text-sm font-semibold text-slate-900">{s.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// LEAD GEN SECTION
// ═══════════════════════════════════════════════════════════════════════
const LeadGenSection = ({ isPro, data = {}, loading }) => {
  const [filter, setFilter] = useState("all");
  const leads     = data.leads || [];
  const responded = leads.filter(l => l.status === "responded").length;
  const opened    = leads.filter(l => l.status === "opened").length;
  const sent      = leads.filter(l => l.status === "sent").length;
  const bounced   = leads.filter(l => l.status === "bounced").length;
  const filtered  = filter === "all" ? leads : leads.filter(l => l.status === filter);
  const total     = leads.length;

  const statusMap = {
    responded: { label: "Responded", color: "#10B981" },
    opened:    { label: "Opened",    color: "#2563EB" },
    sent:      { label: "Sent",      color: "#F59E0B" },
    bounced:   { label: "Bounced",   color: "#EF4444" },
  };

  if (!isPro) return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-slate-900">Lead Generation</h2>
        <p className="text-sm text-slate-500 mt-0.5">Automated outreach to potential customers on your behalf</p>
      </div>
      <div className="bg-white rounded-lg border-2 border-blue-600 p-10 text-center max-w-lg mx-auto">
        <UserSearch className="w-10 h-10 text-blue-600 mx-auto mb-4"/>
        <h3 className="text-base font-bold text-slate-900 mb-2">Lead Gen is a Growth feature</h3>
        <p className="text-sm text-slate-500 mb-6 leading-relaxed">We find 50–100 targeted local leads every month and send personalized outreach on your behalf.</p>
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[{label:"Leads/month",value:"50–100"},{label:"Avg response",value:"~3–5%"},{label:"Fully automated",value:"✓"}].map((s,i) => (
            <div key={i} className="bg-blue-50 rounded-lg p-3">
              <p className="text-base font-bold text-blue-700">{s.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
        <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2.5 rounded-md text-sm transition-colors flex items-center gap-2 mx-auto">
          <Zap className="w-4 h-4"/> Upgrade to Growth
        </button>
        <p className="text-xs text-slate-400 mt-3">3 days free trial, no setup fees</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Lead Generation</h2>
          <p className="text-sm text-slate-500 mt-0.5">We contacted {total} potential customers this month</p>
        </div>
        <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-600">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"/>
          Campaign Active
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Responded", value: responded, status: "responded", color: "#10B981" },
          { label: "Opened",    value: opened,    status: "opened",    color: "#2563EB" },
          { label: "Sent",      value: sent,      status: "sent",      color: "#F59E0B" },
          { label: "Total",     value: total,     status: "all",       color: "#0F172A" },
        ].map((s, i) => (
          <button key={i} onClick={() => setFilter(filter === s.status ? "all" : s.status)}
            className={`rounded-lg border p-4 text-left transition-all hover:shadow-sm ${filter === s.status ? "border-blue-200 bg-blue-50" : "border-slate-200 bg-white"}`}>
            <p className="text-xs text-slate-500 mb-1">{s.label}</p>
            <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          {["all","responded","opened","sent","bounced"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`text-xs font-medium px-2.5 py-1 rounded transition-colors ${filter === f ? "bg-blue-600 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
              {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">No leads match this filter</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left text-xs font-semibold text-slate-500 px-5 py-3">Business</th>
                <th className="text-left text-xs font-semibold text-slate-500 px-5 py-3 hidden sm:table-cell">Contact</th>
                <th className="text-left text-xs font-semibold text-slate-500 px-5 py-3 hidden md:table-cell">Email</th>
                <th className="text-left text-xs font-semibold text-slate-500 px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((lead, i) => {
                const st = statusMap[lead.status] || { label: lead.status, color: "#94A3B8" };
                return (
                  <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3.5">
                      <p className="text-sm font-medium text-slate-900">{lead.name || lead.business}</p>
                      <p className="text-xs text-slate-400">{lead.city}</p>
                    </td>
                    <td className="px-5 py-3.5 hidden sm:table-cell">
                      <p className="text-xs text-slate-600">{lead.contact}</p>
                    </td>
                    <td className="px-5 py-3.5 hidden md:table-cell">
                      <p className="text-xs text-slate-500 truncate max-w-xs">{lead.email}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-xs font-medium px-2 py-0.5 rounded" style={{ color: st.color, background: st.color + "18" }}>
                        {st.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// OUTREACH EMAILS SECTION
// ═══════════════════════════════════════════════════════════════════════
const OutreachEmailsSection = ({ isPro, data = {}, loading }) => {
  const sequences = data.campaigns || data.sequences || [];
  const [expandedSeq, setExpandedSeq] = useState(0);

  if (!isPro) return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-slate-900">Outreach Emails</h2>
        <p className="text-sm text-slate-500 mt-0.5">Automated email sequences sent on your behalf</p>
      </div>
      <div className="bg-white rounded-lg border-2 border-blue-600 p-10 text-center max-w-lg mx-auto">
        <Send className="w-10 h-10 text-blue-600 mx-auto mb-4"/>
        <h3 className="text-base font-bold text-slate-900 mb-2">Outreach Emails is a Growth feature</h3>
        <p className="text-sm text-slate-500 mb-6 leading-relaxed">We send personalized 3-step email sequences to your target leads using dedicated email infrastructure.</p>
        <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2.5 rounded-md text-sm transition-colors flex items-center gap-2 mx-auto">
          <Zap className="w-4 h-4"/> Upgrade to Growth
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-slate-900">Outreach Emails</h2>
        <p className="text-sm text-slate-500 mt-0.5">Email sequences sent to your leads</p>
      </div>
      {sequences.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
          <Send className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
          <p className="text-sm text-slate-500">Email sequences will appear here after lead gen runs</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sequences.map((seq, i) => (
            <div key={i} className="bg-white rounded-lg border border-slate-200">
              <button onClick={() => setExpandedSeq(expandedSeq === i ? -1 : i)}
                className="w-full flex items-center justify-between px-5 py-4">
                <div className="text-left">
                  <p className="text-sm font-semibold text-slate-900">{seq.name || `Sequence ${i+1}`}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{seq.emails?.length || 0} emails • {seq.leads_count || 0} recipients</p>
                </div>
                <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${expandedSeq === i ? "rotate-90" : ""}`}/>
              </button>
              {expandedSeq === i && seq.emails?.length > 0 && (
                <div className="px-5 pb-4 border-t border-slate-100 space-y-3 pt-3">
                  {seq.emails.map((email, j) => (
                    <div key={j} className="flex gap-3 p-3 bg-slate-50 rounded-md">
                      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold flex-shrink-0">{j+1}</div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{email.subject}</p>
                        <p className="text-xs text-slate-500 mt-1 leading-relaxed">{email.body?.slice(0, 120)}...</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// SETTINGS SECTION
// ═══════════════════════════════════════════════════════════════════════
const SettingsSection = ({ user, isPro, navigate }) => {
  const [tab, setTab]               = useState("profile");
  const [name, setName]             = useState(user?.name || "");
  const [bizName, setBizName]       = useState(user?.business_name || "");
  const [website, setWebsite]       = useState(user?.website_url || "");
  const [city, setCity]             = useState(user?.city || "");
  const [curPw, setCurPw]           = useState("");
  const [newPw, setNewPw]           = useState("");
  const [confPw, setConfPw]         = useState("");
  const [notifEmail, setNotifEmail] = useState(user?.notification_prefs?.email ?? true);
  const [notifScore, setNotifScore] = useState(user?.notification_prefs?.score_updates ?? true);
  const [notifPr,    setNotifPr]    = useState(user?.notification_prefs?.pr_updates ?? true);
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState("");

  const sid  = localStorage.getItem("session_id");
  const hdrs = { "Content-Type": "application/json", ...(sid ? { "X-Session-ID": sid } : {}) };

  const saveProfile = async () => {
    setSaving(true); setMsg("");
    try {
      const res = await fetch(`${API}/user/profile`, { method: "PATCH", credentials: "include", headers: hdrs, body: JSON.stringify({ name, business_name: bizName, website_url: website, city }) });
      setMsg(res.ok ? "Saved successfully" : "Save failed");
    } catch { setMsg("Save failed"); }
    setSaving(false);
  };

  const savePassword = async () => {
    if (newPw !== confPw) { setMsg("Passwords don't match"); return; }
    setSaving(true); setMsg("");
    try {
      const res = await fetch(`${API}/user/password`, { method: "PATCH", credentials: "include", headers: hdrs, body: JSON.stringify({ current_password: curPw, new_password: newPw }) });
      setMsg(res.ok ? "Password updated" : "Failed — check current password");
      if (res.ok) { setCurPw(""); setNewPw(""); setConfPw(""); }
    } catch { setMsg("Failed"); }
    setSaving(false);
  };

  const saveNotifs = async () => {
    setSaving(true); setMsg("");
    try {
      const res = await fetch(`${API}/user/notifications`, { method: "PATCH", credentials: "include", headers: hdrs, body: JSON.stringify({ email: notifEmail, score_updates: notifScore, pr_updates: notifPr }) });
      setMsg(res.ok ? "Preferences saved" : "Save failed");
    } catch { setMsg("Save failed"); }
    setSaving(false);
  };

  const tabs = ["profile","password","notifications","billing"];

  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <h2 className="text-lg font-bold text-slate-900">Settings</h2>
        <p className="text-sm text-slate-500 mt-0.5">Manage your account and preferences</p>
      </div>

      <div className="flex border-b border-slate-200">
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${tab === t ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "profile" && (
        <div className="bg-white rounded-lg border border-slate-200 p-6 space-y-4">
          {[
            { label: "Full Name",      value: name,    setter: setName,    type: "text" },
            { label: "Business Name",  value: bizName, setter: setBizName, type: "text" },
            { label: "Website",        value: website, setter: setWebsite, type: "url"  },
            { label: "City",           value: city,    setter: setCity,    type: "text" },
          ].map((f, i) => (
            <div key={i}>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">{f.label}</label>
              <input type={f.type} value={f.value} onChange={e => f.setter(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"/>
            </div>
          ))}
          <div className="pt-2 flex items-center gap-3">
            <button onClick={saveProfile} disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50">
              {saving ? "Saving…" : "Save Changes"}
            </button>
            {msg && <span className={`text-sm ${msg.includes("fail") ? "text-red-500" : "text-emerald-600"}`}>{msg}</span>}
          </div>
        </div>
      )}

      {tab === "password" && (
        <div className="bg-white rounded-lg border border-slate-200 p-6 space-y-4">
          {[
            { label: "Current Password", value: curPw, setter: setCurPw },
            { label: "New Password",     value: newPw, setter: setNewPw },
            { label: "Confirm Password", value: confPw, setter: setConfPw },
          ].map((f, i) => (
            <div key={i}>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">{f.label}</label>
              <input type="password" value={f.value} onChange={e => f.setter(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"/>
            </div>
          ))}
          <div className="pt-2 flex items-center gap-3">
            <button onClick={savePassword} disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50">
              {saving ? "Saving…" : "Update Password"}
            </button>
            {msg && <span className={`text-sm ${msg.includes("fail") || msg.includes("match") ? "text-red-500" : "text-emerald-600"}`}>{msg}</span>}
          </div>
        </div>
      )}

      {tab === "notifications" && (
        <div className="bg-white rounded-lg border border-slate-200 p-6 space-y-4">
          {[
            { label: "Email notifications",  sub: "Receive emails for wins and updates", value: notifEmail, setter: setNotifEmail },
            { label: "Score updates",         sub: "Get notified when your score changes", value: notifScore, setter: setNotifScore },
            { label: "PR & blog updates",     sub: "Get notified when press releases or blog posts go live", value: notifPr, setter: setNotifPr },
          ].map((f, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
              <div>
                <p className="text-sm font-medium text-slate-900">{f.label}</p>
                <p className="text-xs text-slate-400 mt-0.5">{f.sub}</p>
              </div>
              <button onClick={() => f.setter(!f.value)}
                className={`relative w-10 h-5 rounded-full transition-colors ${f.value ? "bg-blue-600" : "bg-slate-200"}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${f.value ? "translate-x-5" : "translate-x-0.5"}`}/>
              </button>
            </div>
          ))}
          <div className="pt-2 flex items-center gap-3">
            <button onClick={saveNotifs} disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50">
              {saving ? "Saving…" : "Save Preferences"}
            </button>
            {msg && <span className={`text-sm ${msg.includes("fail") ? "text-red-500" : "text-emerald-600"}`}>{msg}</span>}
          </div>
        </div>
      )}

      {tab === "billing" && (
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center justify-between pb-4 border-b border-slate-100">
            <div>
              <p className="text-sm font-semibold text-slate-900">Current Plan</p>
              <p className="text-xs text-slate-400 mt-0.5">{isPro ? "Growth" : "Basic"} — {user?.subscription_status || "active"}</p>
            </div>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded ${isPro ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
              {isPro ? "Growth" : "Basic"}
            </span>
          </div>
          {!isPro && (
            <div className="mt-4">
              <button onClick={() => navigate("/pricing")}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors flex items-center gap-2">
                <Zap className="w-4 h-4"/> Upgrade to Growth
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// HELP SECTION
// ═══════════════════════════════════════════════════════════════════════
const HelpSection = () => {
  const [ticket, setTicket]     = useState("");
  const [category, setCategory] = useState("general");
  const [sent, setSent]         = useState(false);
  const [sending, setSending]   = useState(false);

  const submitTicket = async () => {
    if (!ticket.trim()) return;
    setSending(true);
    try {
      const sid = localStorage.getItem("session_id");
      const res = await fetch(`${API}/support/ticket`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...(sid ? { "X-Session-ID": sid } : {}) },
        body: JSON.stringify({ message: ticket, category }),
      });
      if (res.ok) { setSent(true); setTicket(""); }
    } catch {}
    setSending(false);
  };

  const faqs = [
    { q: "How is my ORDO AI score calculated?", a: "Your score is a weighted average across 11 AI platforms and directories. ChatGPT has the highest weight (20%) followed by Google Gemini (12%). Online Presence (10%) covers your directory listings and citations." },
    { q: "How often does my score update?", a: "Scores update every Monday at 6am UTC. You'll also get a fresh scan on day 2 of your trial, and immediately after completing onboarding." },
    { q: "When will I see my first press release?", a: "Your first PR is generated automatically within 24 hours of completing onboarding. Once our team reviews and approves it, it will appear in your dashboard." },
    { q: "How do directory submissions work?", a: "We submit your business to 15+ AI-indexed directories every week. Each submission appears in your Submissions section with its current status." },
    { q: "What is the difference between Basic and Growth?", a: "Basic includes scoring, PR (1/month), blog posts (1/month), and directory submissions. Growth adds lead generation, automated email outreach, 3 PRs/month, and 2 blog posts/month." },
  ];

  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <h2 className="text-lg font-bold text-slate-900">Help & Support</h2>
        <p className="text-sm text-slate-500 mt-0.5">Get answers or contact our team</p>
      </div>

      {/* FAQ */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">Frequently Asked Questions</h3>
        </div>
        {faqs.map((faq, i) => (
          <div key={i} className="border-b border-slate-100 last:border-0">
            <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
              className="w-full flex items-center justify-between px-5 py-3.5 text-left">
              <span className="text-sm font-medium text-slate-900">{faq.q}</span>
              <ChevronRight className={`w-4 h-4 text-slate-400 flex-shrink-0 ml-3 transition-transform ${openFaq === i ? "rotate-90" : ""}`}/>
            </button>
            {openFaq === i && (
              <div className="px-5 pb-4">
                <p className="text-sm text-slate-500 leading-relaxed">{faq.a}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Contact */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-4">Contact Support</h3>
        {sent ? (
          <div className="flex items-center gap-2 text-emerald-600 text-sm">
            <CheckCircle className="w-4 h-4"/> Ticket submitted — we'll respond within 24 hours
          </div>
        ) : (
          <div className="space-y-3">
            <select value={category} onChange={e => setCategory(e.target.value)}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500">
              <option value="general">General question</option>
              <option value="billing">Billing</option>
              <option value="technical">Technical issue</option>
              <option value="score">Score question</option>
              <option value="pr">Press release</option>
            </select>
            <textarea value={ticket} onChange={e => setTicket(e.target.value)} rows={4}
              placeholder="Describe your question or issue..."
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-700 resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"/>
            <button onClick={submitTicket} disabled={sending || !ticket.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50">
              {sending ? "Sending…" : "Submit Ticket"}
            </button>
          </div>
        )}
        <p className="text-xs text-slate-400 mt-4">Or email us directly at <a href="mailto:support@theordo.io" className="text-blue-600 hover:underline">support@theordo.io</a></p>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════════
const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const initialSection = (() => {
    const params = new URLSearchParams(location.search);
    const s = params.get("section");
    const valid = ["dashboard","score","competitors","submissions","press","blog","leads","emails","settings","help"];
    return valid.includes(s) ? s : "dashboard";
  })();

  const [activeSection, setActiveSection] = useState(initialSection);
  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [accountOpen,   setAccountOpen]   = useState(false);
  const [notifOpen,     setNotifOpen]     = useState(false);
  const [user,          setUser]          = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [trialDaysLeft, setTrialDaysLeft] = useState(0);

  const isPro      = ["growth","dfy","dfy_monthly","dfy_yearly"].includes(user?.plan_intent || user?.subscription_tier);
  const isTrialing = user?.subscription_status === "trialing";

  useEffect(() => {
    const sid = localStorage.getItem("session_id");
    const fetchUser = async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: "include", headers: sid ? { "X-Session-ID": sid } : {} });
        if (res.ok) {
          const data = await res.json();
          setUser(data);
          if (data.trial_ends_at) {
            const days = Math.max(0, Math.ceil((new Date(data.trial_ends_at) - Date.now()) / 86400000));
            setTrialDaysLeft(days);
          }
        } else { navigate("/login"); }
      } catch { navigate("/login"); }
      finally { setLoading(false); }
    };
    fetchUser();
  }, []);

  const handleLogout = async () => {
    const sid = localStorage.getItem("session_id");
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include", headers: sid ? { "X-Session-ID": sid } : {} });
    localStorage.removeItem("session_id");
    navigate("/login");
  };

  const { data: apiData, loading: apiLoading, refresh } = useDashboardData(user);

  const overviewData   = apiData.overview      || {};
  const scoreData      = apiData.score         || {};
  const competitorData = apiData.competitors   || {};
  const submissionData = apiData.submissions   || {};
  const prData         = apiData.pressReleases || {};
  const blogData       = apiData.blogPosts     || {};
  const notifData      = apiData.notifications || {};
  const leadsData      = apiData.leads         || {};
  const emailData      = apiData.emails        || {};

  const score         = overviewData.score?.current ?? scoreData.master_score ?? 0;
  const scoreChange   = overviewData.score?.change  ?? 0;
  const notifications = notifData.notifications ?? [];
  const unreadCount   = notifData.unread         ?? 0;
  const prPending     = (prData.press_releases ?? []).filter(p => p.status === "ai_generated" || p.status === "reviewing").length;
  const blogPending   = (blogData.posts ?? []).filter(b => b.status === "reviewing" || b.status === "ready").length;
  const subLive       = submissionData.counts?.live ?? 0;
  const subTotal      = (submissionData.submissions ?? []).length || 0;

  const nav = (section) => { setActiveSection(section); setSidebarOpen(false); };

  if (loading) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"/>
        <p className="text-sm text-slate-500">Loading your dashboard…</p>
      </div>
    </div>
  );

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-4 py-4 border-b border-slate-200 flex items-center justify-between">
        <span className="text-base font-bold text-slate-900 tracking-tight">ORDO AI</span>
        <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-400 hover:text-slate-600">
          <X className="w-4 h-4"/>
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-3">
        <NavItem icon={LayoutDashboard} label="Dashboard" active={activeSection === "dashboard"} onClick={() => nav("dashboard")}/>

        <NavSection label="GEO & Visibility"/>
        <NavItem icon={BarChart2}  label="AI Score"    active={activeSection === "score"}       onClick={() => nav("score")}/>
        <NavItem icon={Users}      label="Competitors" active={activeSection === "competitors"} onClick={() => nav("competitors")}/>
        <NavItem icon={Globe}      label="Submissions" active={activeSection === "submissions"} onClick={() => nav("submissions")} badge={subTotal > subLive && subTotal > 0 ? String(subTotal - subLive) : null}/>

        <NavSection label="Content & PR"/>
        <NavItem icon={Newspaper}  label="Press Releases" active={activeSection === "press"} onClick={() => nav("press")} badge={prPending > 0 ? String(prPending) : null}/>
        <NavItem icon={BookOpen}   label="Blog Posts"     active={activeSection === "blog"}  onClick={() => nav("blog")}  badge={blogPending > 0 ? "New" : null}/>

        <NavSection label="Leads & Outreach"/>
        <NavItem icon={UserSearch} label="Lead Gen"        active={activeSection === "leads"}  onClick={() => nav("leads")}/>
        <NavItem icon={Send}       label="Outreach Emails" active={activeSection === "emails"} onClick={() => nav("emails")}/>

        <NavSection label="Account"/>
        <NavItem icon={Settings}    label="Settings"     active={activeSection === "settings"} onClick={() => nav("settings")}/>
        <NavItem icon={HelpCircle}  label="Help & Support" active={activeSection === "help"} onClick={() => nav("help")}/>
      </nav>

      {/* Upgrade CTA */}
      {!isPro && (
        <div className="px-3 py-3 border-t border-slate-200">
          <div className="bg-blue-600 rounded-lg p-4 text-white">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Zap className="w-3.5 h-3.5 text-yellow-300"/>
              <span className="text-xs font-bold">Upgrade to Growth</span>
            </div>
            <p className="text-xs text-blue-100 mb-3 leading-relaxed">Unlock automation, lead gen, and done-for-you PR</p>
            <button onClick={() => navigate("/pricing")}
              className="w-full bg-white text-blue-600 text-xs font-bold py-1.5 rounded transition-colors hover:bg-blue-50">
              See Growth Plan
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 flex" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>

      {/* Sidebar desktop */}
      <aside className="hidden lg:flex flex-col w-60 bg-white border-r border-slate-200 fixed h-full z-30">
        <SidebarContent/>
      </aside>

      {/* Sidebar mobile */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-60 bg-white border-r border-slate-200 flex flex-col h-full">
            <SidebarContent/>
          </div>
          <div className="flex-1 bg-black/30" onClick={() => setSidebarOpen(false)}/>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-60 flex flex-col min-h-screen">

        {/* Topbar */}
        <header className="bg-white border-b border-slate-200 h-14 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-500">
              <Menu className="w-5 h-5"/>
            </button>
            <h1 className="text-sm font-semibold text-slate-900 capitalize">
              {activeSection === "dashboard" ? "Dashboard" : activeSection === "score" ? "AI Score" : activeSection === "press" ? "Press Releases" : activeSection.replace("-", " ")}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            {isTrialing && (
              <div className="hidden sm:flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-md">
                <Clock className="w-3 h-3"/>
                {trialDaysLeft} days left in trial
              </div>
            )}

            {/* Notifications */}
            <div className="relative">
              <button onClick={() => { setNotifOpen(!notifOpen); setAccountOpen(false); }}
                className="relative w-8 h-8 flex items-center justify-center rounded-md hover:bg-slate-100 transition-colors text-slate-500">
                <Bell className="w-4 h-4"/>
                {unreadCount > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"/>}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-10 w-80 bg-white rounded-lg border border-slate-200 shadow-lg z-50">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-900">Notifications</span>
                    {unreadCount > 0 && (
                      <button onClick={async () => {
                        const sid = localStorage.getItem("session_id");
                        await fetch(`${API}/notifications/read-all`, { method: "PATCH", credentials: "include", headers: sid ? { "X-Session-ID": sid } : {} });
                        refresh("notifications", "/notifications");
                      }} className="text-xs text-blue-600 font-medium hover:underline">Mark all read</button>
                    )}
                  </div>
                  <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                    {notifications.length === 0 ? (
                      <p className="text-xs text-slate-400 py-6 text-center">No notifications yet</p>
                    ) : notifications.map((n, i) => {
                      const iconMap  = { win: Star, info: Bot, score: TrendingUp, pr: Newspaper, blog: BookOpen, lead: UserSearch };
                      const colorMap = { win: "#10B981", info: "#2563EB", score: "#10B981", pr: "#F59E0B", blog: "#7C3AED", lead: "#2563EB" };
                      const t = n.type || "info";
                      const Icon = iconMap[t] || Bot;
                      const color = colorMap[t] || "#2563EB";
                      return (
                        <div key={i} className="flex items-start gap-3 px-4 py-3">
                          <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: color + "18" }}>
                            <Icon className="w-3.5 h-3.5" style={{ color }}/>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-700 leading-relaxed">{n.message || n.title}</p>
                            <p className="text-xs text-slate-400 mt-0.5">{timeAgo(n.created_at)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Account */}
            <div className="relative">
              <button onClick={() => { setAccountOpen(!accountOpen); setNotifOpen(false); }}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-100 transition-colors">
                <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                  {user?.name?.[0]?.toUpperCase() || "U"}
                </div>
                <span className="hidden sm:block text-sm font-medium text-slate-700 max-w-24 truncate">{user?.name || "User"}</span>
                <ChevronDown className="w-3 h-3 text-slate-400"/>
              </button>
              {accountOpen && (
                <div className="absolute right-0 top-10 w-44 bg-white rounded-lg border border-slate-200 shadow-lg z-50 overflow-hidden">
                  <div className="px-3 py-2.5 border-b border-slate-100">
                    <p className="text-xs font-semibold text-slate-900 truncate">{user?.name}</p>
                    <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                  </div>
                  <div className="py-1">
                    <button onClick={() => { nav("settings"); setAccountOpen(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50">
                      <Settings className="w-3.5 h-3.5 text-slate-400"/> Settings
                    </button>
                    <div className="border-t border-slate-100 mt-1 pt-1">
                      <button onClick={handleLogout}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-red-500 hover:bg-red-50">
                        <LogOut className="w-3.5 h-3.5"/> Log out
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-5 sm:p-6">

          {/* Trial banner mobile */}
          {isTrialing && (
            <div className="sm:hidden flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 mb-5">
              <span className="text-xs font-medium text-amber-700">{trialDaysLeft} days left in your trial</span>
              <button onClick={() => navigate("/pricing")} className="text-xs font-bold text-amber-700 underline">Upgrade</button>
            </div>
          )}

          {/* ── DASHBOARD OVERVIEW ─────────────────────────────────────── */}
          {activeSection === "dashboard" && (
            <div className="space-y-5">

              {/* Welcome row */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">
                    Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
                  </h2>
                  <p className="text-sm text-slate-500 mt-0.5">Here's your AI visibility overview</p>
                </div>
                <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500 bg-white border border-slate-200 rounded-md px-3 py-1.5">
                  <Clock className="w-3 h-3 text-slate-400"/>
                  Scans every Monday
                </div>
              </div>

              {/* Stat cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={BarChart2}    iconColor="#2563EB" label="ORDO AI Score"     value={score}  change={scoreChange} suffix=" pts"/>
                <StatCard icon={Globe}        iconColor="#10B981" label="Directories Live"  value={subLive}/>
                <StatCard icon={Newspaper}    iconColor="#F59E0B" label="Press Releases"    value={(prData.press_releases ?? []).filter(p => p.status === "published").length}/>
                <StatCard icon={BookOpen}     iconColor="#7C3AED" label="Blog Posts Live"   value={(blogData.posts ?? []).filter(b => b.status === "published").length}/>
              </div>

              {/* Chart + Score */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Score History</h3>
                      <p className="text-xs text-slate-400 mt-0.5">Your ORDO AI score over time</p>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded ${scoreChange >= 0 ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
                      {scoreChange >= 0 ? "+" : ""}{scoreChange} pts this week
                    </span>
                  </div>
                  <ResponsiveContainer width="100%" height={190}>
                    <AreaChart data={overviewData.score_history || []}>
                      <defs>
                        <linearGradient id="sg2" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#2563EB" stopOpacity={0.08}/>
                          <stop offset="95%" stopColor="#2563EB" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F8FAFC" vertical={false}/>
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false}/>
                      <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} domain={["auto","auto"]}/>
                      <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "6px", fontSize: "12px" }} formatter={(v) => [`${v} pts`, "Score"]}/>
                      <Area type="monotone" dataKey="score" stroke="#2563EB" strokeWidth={2} fill="url(#sg2)" dot={false} activeDot={{ r: 4, fill: "#2563EB" }}/>
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Score widget */}
                <div className="bg-white rounded-lg border border-slate-200 p-5 flex flex-col">
                  <h3 className="text-sm font-semibold text-slate-900 mb-4">Current Score</h3>
                  <div className="flex-1 flex items-center justify-center">
                    <ScoreRing score={score} size={130}/>
                  </div>
                  <div className="mt-4 space-y-2 pt-4 border-t border-slate-100">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">vs last scan</span>
                      <span className={scoreChange >= 0 ? "text-emerald-600 font-semibold" : "text-red-500 font-semibold"}>
                        {scoreChange >= 0 ? "+" : ""}{scoreChange} pts
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Next scan</span>
                      <span className="text-slate-700 font-medium">Monday 6am UTC</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Platform breakdown */}
              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-slate-900">Platform Breakdown</h3>
                  <button onClick={() => nav("score")} className="text-xs text-blue-600 font-medium hover:underline flex items-center gap-1">
                    Full report <ChevronRight className="w-3 h-3"/>
                  </button>
                </div>
                <div className="space-y-3">
                  {PLATFORM_META.map((p) => {
                    const raw = (scoreData.platform_scores || {})[p.key] ?? 0;
                    const pct = Math.min(raw, 100);
                    return (
                      <div key={p.key} className="flex items-center gap-3">
                        <div className="flex items-center gap-2 w-32 flex-shrink-0">
                          <PlatformLogo platform={p} size={18}/>
                          <span className="text-xs text-slate-600 truncate">{p.name}</span>
                        </div>
                        <div className="flex-1 bg-slate-100 rounded-full h-1.5">
                          <div className="h-1.5 rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: p.color }}/>
                        </div>
                        <span className="text-xs font-medium text-slate-700 w-8 text-right">{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Activity + Funnel cards */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Recent activity */}
                <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-900">Recent Activity</h3>
                    <button onClick={() => setNotifOpen(true)} className="text-xs text-blue-600 font-medium hover:underline">View all</button>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {notifications.length === 0 ? (
                      <p className="text-xs text-slate-400 py-4 text-center">No activity yet — complete your first scan to see results</p>
                    ) : notifications.slice(0, 5).map((n, i) => {
                      const iconMap  = { win: Star, info: Bot, score: TrendingUp, pr: Newspaper, blog: BookOpen, lead: UserSearch };
                      const colorMap = { win: "#10B981", info: "#2563EB", score: "#10B981", pr: "#F59E0B", blog: "#7C3AED", lead: "#2563EB" };
                      const t = n.type || "info";
                      const Icon = iconMap[t] || Bot;
                      const color = colorMap[t] || "#2563EB";
                      return (
                        <div key={i} className="flex items-start gap-3 py-3">
                          <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: color + "18" }}>
                            <Icon className="w-3.5 h-3.5" style={{ color }}/>
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-slate-700">{n.message || n.title}</p>
                            <p className="text-xs text-slate-400 mt-0.5">{timeAgo(n.created_at)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Funnel summary */}
                <div className="space-y-3">
                  {[
                    { icon: Newspaper, label: "Press Releases", color: "#2563EB", stats: [{ label: "Published", value: (prData.press_releases||[]).filter(p=>p.status==="published").length }, { label: "In review", value: prPending }] },
                    { icon: Globe, label: "Directory Submissions", color: "#10B981", stats: [{ label: "Live", value: submissionData.counts?.live || 0 }, { label: "Total", value: subTotal }] },
                    { icon: BookOpen, label: "Blog Posts", color: "#7C3AED", stats: [{ label: "Published", value: (blogData.posts||[]).filter(b=>b.status==="published").length }, { label: "Pending", value: blogPending }] },
                  ].map((card, i) => (
                    <div key={i} className="bg-white rounded-lg border border-slate-200 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: card.color + "18" }}>
                          <card.icon className="w-3.5 h-3.5" style={{ color: card.color }}/>
                        </div>
                        <span className="text-xs font-semibold text-slate-900">{card.label}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {card.stats.map((s, j) => (
                          <div key={j}>
                            <p className="text-lg font-bold text-slate-900">{s.value}</p>
                            <p className="text-xs text-slate-400">{s.label}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── SECTION ROUTING ─────────────────────────────────────── */}
          {activeSection === "score"       && <AIScoreSection score={score} isPro={isPro} scoreData={scoreData} loading={apiLoading.score}/>}
          {activeSection === "competitors" && <CompetitorsSection isPro={isPro} data={competitorData} loading={apiLoading.competitors}/>}
          {activeSection === "submissions" && <SubmissionsSection data={submissionData} loading={apiLoading.submissions} refresh={() => refresh("submissions","/submissions")}/>}
          {activeSection === "press"       && <PressReleasesSection isPro={isPro} data={prData} loading={apiLoading.pressReleases} refresh={() => refresh("pressReleases","/press-releases")}/>}
          {activeSection === "blog"        && <BlogPostsSection isPro={isPro} data={blogData} loading={apiLoading.blogPosts} refresh={() => refresh("blogPosts","/blog-posts")}/>}
          {activeSection === "leads"       && <LeadGenSection isPro={isPro} data={leadsData} loading={apiLoading.leads}/>}
          {activeSection === "emails"      && <OutreachEmailsSection isPro={isPro} data={emailData} loading={apiLoading.emails}/>}
          {activeSection === "settings"    && <SettingsSection user={user} isPro={isPro} navigate={navigate}/>}
          {activeSection === "help"        && <HelpSection/>}

        </main>
      </div>

      {/* Click outside */}
      {(accountOpen || notifOpen) && (
        <div className="fixed inset-0 z-10" onClick={() => { setAccountOpen(false); setNotifOpen(false); }}/>
      )}
    </div>
  );
};

export default Dashboard;
